// Story 7.12 Iteration 2 (ADO-911), DR-2 — the PDP hero card.
//
// The PO-approved Figma (1169:7024) hero: product image with a zoom affordance on
// the left (it opens the full-size image in a fading dialog); on the right the
// title, the status pill (In Stock), the favourite heart,
// the price block, a four-cell detail strip (Pack/Case, RSP, Margin, Last Ordered; ADO-2249,
// BWG feedback 5 Nov), an On Order row, and the primary actions (Add, and the cheaper-on-WOW action when
// it applies, Story 11.12). The
// sample MULTI-BUY pill and Offer row are deleted (Pricing migration 32,
// ADO-3985 T10).
//
// WHAT IS REAL vs FIXTURE (Deviation D-1, OQ-10 "ship the hero display fields as
// FIXTURES"):
//   REAL    — name, image, pack size (Story 7.1/7.4 read model); the In Stock pill
//             and the supplier-UNAVAILABLE add gate (Story 7.3 client-side
//             availability); the favourite heart (Story 7.5); the status band and the
//             Add itself (Story 7.9 `useBasketStore`); the On Order row (Story 8.12
//             `useOnOrderFor`, the Customer's open orders).
//   PRICE: the unit price, through the shared `ProductPrice` (Pricing migration 17,
//             ADO-2248): the stand-in `provisionalUnitPrice` while
//             `VITE_REAL_PRICES_ENABLED` is off, the price quotes endpoint's figure when
//             it is on. The RRP follows the price (ADO-2248 Q2): the fixture with the
//             flag off, the endpoint's RRP with it on (the pack stays a fixture). So does the
//             margin (Story 11.10, ADO-1961): the sample with the flag off, the quote's with it on.
//   FIXTURE: RRP and Margin (flag off), Pack/Case (with no real pack), Offer
//             (`productDetailSample`); see the gap register in the story
//             (each field's owner is in `productDetailSample.ts`'s gap register).
//   PLACEHOLDER: Last Ordered, the cart's empty-cell glyph until Story 7.23 (ADO-1712)
//             wires the real date.
//
// Pricing migration 32 (ADO-3985), T12: the offer strip per screen size (frame 05, t/04, m/04). From
// 1024px a promotion's wide text and the deal's wide strip with "See deal"; below 1024px the
// compact promotion text and the compact deal strip as one button. A product with both shows both,
// the promotion first (A3). Both deal strips hand over to the page (`onSeeDeal`: open that deal in
// the deal section, scroll to it and highlight it); neither opens the dialog.
//
// The cart action follows the product card (PO decision 2026-10-02): Add while the
// session depot's cart holds none of the product, the cart line's own stepper once it
// holds some, never both. The stepper edits the line by the cart row's rules.

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  ConflictMessage,
  Icon,
  Modal,
  PickedFreshPill,
  QuantityStepper,
  StatusBadge,
} from '../../design-system';
import type { QuantityStepperCommit, StatusTone } from '../../design-system';
import type { ProductSummary } from '../../api/browse.client';
import type { AvailabilityBand } from '../../api/availability.client';
import { useAvailabilityStore } from '../../availability/useAvailabilityStore';
import { PICKED_FRESH_LABEL, showsPickedFresh } from '../../availability/pickedFresh';
import { useFavouritesStore } from '../../favourites/useFavouritesStore';
import { useBasketStore, SUPPLIER_UNAVAILABLE_MESSAGE } from '../../basket/useBasketStore';
import type { ConflictIntent } from '../../basket/conflictCopy';
import { useLineConflictMessage } from '../../basket/useLineConflictMessage';
import { useAddConfirmation } from '../../basket/useAddConfirmation';
import { NEVER_ORDERED_GLYPH, NOT_AVAILABLE_YET } from '../../basket/CartLineRow';
import {
  isAtLineMaximum,
  sanitiseQuantityInput,
  typedLineQuantity,
} from '../../basket/lineQuantity';
import { useSessionDepotId } from '../../availability/session-depot';
import { useOnOrderFor } from '../../onOrder/useOnOrder';
import { onOrderChipText, onOrderDetailText } from '../../onOrder/onOrderDisplay';
import { OnOrderChip } from '../../onOrder/OnOrderChip';
import { ProductHeroImage, useProductHdImage } from './ProductHeroImage';
import { safeImageHref } from '../Browse/safeImageHref';
import { productDetailSample } from './productDetailSample';
import {
  ProductPrice,
  usePriceBlocksAdd,
  useProductMargin,
  useProductRrp,
  useProductWasRrp,
} from '../../pricing/ProductPrice';
import { EmptyValue } from '../../shared/EmptyValue';
import { realPricesEnabled } from '../../pricing/realPrices';
import { usePriceEntry } from '../../pricing/usePriceStore';
import { ContractPricePill } from '../../pricing/ContractPricePill';
import {
  explainedPrice,
  listOfferText,
  shownEntry,
  spokenRsp,
} from '../../pricing/priceExplanation';
import { useMultibuyDealsEntry } from '../../multibuy/useMultibuyDealsStore';
import {
  cardDeal,
  compactDealText,
  wideDealText,
  withMoreOffers,
} from '../../multibuy/multibuyDealText';
import { compactStripName, seeDealName } from '../../multibuy/spokenDealText';
import { useIsNarrowViewport } from '../../basket/useIsNarrowViewport';
import { packCaseLabel } from '../../basket/packCase';
import { useFreeProductName } from '../../multibuy/useFreeProductName';
import { TABLET_DOWN_MEDIA_QUERY } from '../../design-system/breakpoints';
import { MarginInfo, usePdpMarginInfo } from '../../pricing/MarginInfo';
import { useProductDetailStore } from '../../product-detail/useProductDetailStore';
import { formatMoney } from '../../shared/money';
import { WowOfferAction } from './WowOfferAction';
import styles from './ProductHero.module.css';

/** The hero's price with nothing to explain: flag off, or no price to show. */
const NOT_EXPLAINED = {
  promotion: false,
  contract: false,
  wasPrice: undefined,
  spoken: null,
  deposit: null,
} as const;

/** Band → StatusBadge tone + label. Meaning is in the LABEL, never colour alone. */
const BAND_PRESENTATION: Record<AvailabilityBand, { tone: StatusTone; label: string }> = {
  IN_STOCK: { tone: 'success', label: 'In Stock' },
  LOW_STOCK: { tone: 'warning', label: 'Low Stock' },
  OUT_OF_STOCK: { tone: 'error', label: 'Out of Stock' },
};

export interface ProductHeroProps {
  readonly product: ProductSummary;
  /** "See deal" on the multi-buy strip (ADO-3985, T12): the page scrolls to the deal section and
   *  highlights it. */
  readonly onSeeDeal?: (offerCode: string) => void;
}

export function ProductHero({ product, onSeeDeal }: ProductHeroProps) {
  const sample = productDetailSample(product.sku);
  const realPrices = realPricesEnabled();
  // ADO-2248 Q2 (decided 2026-10-20): the RSP follows the price. Flag off, the sample RSP. Flag
  // on, the real one (empty when there is none, or while the price is loading, unpriced or
  // failed). ADO-2249 (BWG feedback, 5 Nov; Figma 2026-11-03-2): the RSP, the pack and the margin
  // are the detail strip's cells, `Pack/Case | RSP | Margin | Last Ordered`, and the price row
  // keeps only the price, the struck was price, the `Contract price` pill and the deposit. The
  // pack reads `{retailUnits} / {packSize}` (the sample only when the catalogue holds neither).
  // Story 11.10 (ADO-1961), S-9 and S-11: the margin is the sample with the flag off and the
  // quote's with it on (`—`, spoken "not available", when it cannot be computed), and there is no
  // POR in either flag state. Pricing migration 37 (ADO-4034, DEC-1, DEC-4): with the flag on, a
  // was RSP on the quote shows struck before the RSP in the RSP cell, both figures hidden from
  // assistive tech, and a visually hidden sibling reads them in words (DEC-7).
  const packCase = packCaseLabel(product.retailUnits, product.packSize) ?? sample.packCase;
  const realRrp = useProductRrp(product.sku);
  const rsp = realPrices ? realRrp : sample.rrp;
  const realWasRrp = useProductWasRrp(product.sku);
  const wasRsp = realPrices ? realWasRrp : null;
  const margin = useProductMargin(product.sku);
  const marginValue: ReactNode = !realPrices ? (
    `${sample.marginPct}%`
  ) : margin.kind === 'shown' ? (
    margin.text
  ) : margin.kind === 'unavailable' ? (
    <EmptyValue spoken="not available" />
  ) : null;
  const rspValue: ReactNode =
    rsp === null ? null : wasRsp === null ? (
      rsp
    ) : (
      <>
        <span className={styles.rspGroup} aria-hidden="true">
          <s className={styles.wasRsp} data-was-rsp="">
            {wasRsp}
          </s>{' '}
          <span data-rsp="">{rsp}</span>
        </span>
        <span className={styles.visuallyHidden}>{spokenRsp(rsp, wasRsp)}</span>
      </>
    );
  // The detail strip's Margin cell (S-9): absent where the store does not see its margin; in its
  // place with an empty value while the price is loading, failed or unpriced.
  const marginCell: ReactNode | undefined =
    realPrices && margin.kind === 'hidden'
      ? undefined
      : realPrices && margin.kind === 'leftOut'
        ? null
        : marginValue;
  // ADO-2249: the price explains itself with the flag on and a price to show. A promotion's
  // price is red, its was price struck after it; a contract's carries the `Contract price` pill;
  // a promotion or contract is read out in words (DEC-15), the figures it replaces hidden.
  const priceEntry = usePriceEntry(product.sku);
  const priced = realPrices ? shownEntry(priceEntry) : null;
  const { promotion, contract, wasPrice, spoken, deposit } =
    priced === null ? NOT_EXPLAINED : explainedPrice(priced);
  // ADO-3985 T12: below 1024px (`DESKTOP_MIN_WIDTH`) the promotion text is compact (t/04, m/04),
  // without "Promotion ·"; from 1024px it is the wide text. Without matchMedia (tests, SSR) the
  // hook reports the desktop layout.
  const desktop = !useIsNarrowViewport(TABLET_DOWN_MEDIA_QUERY);
  // Story 11.10's second PR (ADO-1961, MI-1 to MI-3, MI-9): the margin info button beside the
  // detail strip's Margin label, the only place the margin shows. Its panel uses the product detail's VAT
  // rate (the VAT row's); it is the Default panel from 1024px, the Touch one on tablet and the
  // bottom sheet on phones (below 768px, the cart's phone breakpoint).
  const vatRate = useProductDetailStore((s) => s.detailBySku[product.sku]?.detail?.vatRate ?? null);
  const marginInfo = usePdpMarginInfo(product.sku, vatRate);
  const phone = useIsNarrowViewport();
  const marginInfoSize = desktop ? 'default' : phone ? 'sheet' : 'touch';
  // ADO-2249 (BWG feedback, 5 Nov): the promotion's saving is on the cases in the cart, at least
  // one, from the live price entry (an optimistic add's cart line has no was price yet), as the
  // side cart's `Save` pill works it out.
  const inCart = useBasketStore((s) => s.quantityBySku.get(product.sku) ?? 0);
  const offerText = priced === null ? null : listOfferText(priced, { prefix: desktop, inCart });
  // ADO-3985 T12: the product's live multi-buy deal has its own strip, after the promotion's
  // when there is one (A3: "The promotion strip sits first: it changes the price the shop pays
  // now; the multi-buy needs more cases"). It shows once the price has answered (priced or
  // unpriced), as on the card. A contract price does not block it: here the `Contract price` pill
  // sits in the price row. No deal while the deal data is loading, failed or absent (AC6), and
  // none with the flag off (D-5). The strip shows the deal the card picks, plus "+N more" for the
  // product's other deals (R4 + R5 05), and "See deal" opens that deal in the deal section.
  const dealsEntry = useMultibuyDealsEntry(product.sku);
  const priceAnswered = priceEntry?.state === 'priced' || priceEntry?.state === 'unpriced';
  const liveDeals = realPrices && dealsEntry?.state === 'ready' ? dealsEntry.deals : [];
  const shownDeal = priceAnswered ? cardDeal(liveDeals) : null;
  // The wide strip's "Free: {product}" (R1 05s, R4 + R5 05; D-15), named only from 1024px.
  const wideFreeName = useFreeProductName(desktop ? shownDeal : null);
  const otherDeals = liveDeals.length - 1;
  const seeDeal = () => {
    if (shownDeal !== null) onSeeDeal?.(shownDeal.multibuyOfferCode);
  };

  const availability = useAvailabilityStore((s) => s.entries[product.sku]);
  const band = availability?.band;
  const supplierUnavailable = availability?.status === 'UNAVAILABLE';

  const isFavourite = useFavouritesStore((s) => s.favouriteSkus.has(product.sku));
  const favouritePending = useFavouritesStore((s) => s.pending.has(product.sku));
  const addFavourite = useFavouritesStore((s) => s.add);
  const removeFavourite = useFavouritesStore((s) => s.remove);

  // Story 8.12 (ADO-2021) — loaded by `ProductDetail` (`useOnOrder`), read per SKU here.
  const onOrder = useOnOrderFor(product.sku);
  const sessionDepotId = useSessionDepotId();
  const basketPending = useBasketStore((s) => s.pending.has(product.sku));
  // ADO-2248 PS-2: the Add waits for the price, with main's disabled look.
  const priceBlocksAdd = usePriceBlocksAdd(product.sku);
  const basketError = useBasketStore((s) => s.mutationErrors.get(product.sku) ?? null);
  // AC7 — a successful add here opens the Linked Product upsell (one shared seam
  // inside `useAddConfirmation`). ADO-911 design refresh: the PDP is the ONLY
  // surface that still opens the full MODAL; search/listing adds open the toast.
  const { confirmed, confirm } = useAddConfirmation(product.sku, {
    productName: product.name,
    upsellVariant: 'modal',
  });

  const setLineQuantity = useBasketStore((s) => s.setQuantity);
  // True from an Add press until that add settles. The store shows the new line
  // optimistically at once, so without this the hero would swap to the stepper and,
  // on a failed add, back to Add; it keeps a disabled Add on screen instead.
  const [firstAddInFlight, setFirstAddInFlight] = useState(false);
  // An unavailable product keeps a disabled Add even when it is in the cart: the cart
  // row is where that line is edited or removed.
  const showsCartStepper = inCart > 0 && !firstAddInFlight && !supplierUnavailable;

  // Swapping Add for the stepper (and back) unmounts the focused control, which would
  // drop focus to <body>. A swap asks for focus on a control by its `data-control`; it is
  // taken as soon as that control is on screen (this render or a later one).
  const actionsRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<string | null>(null);
  const focusActionControl = (control: string): boolean => {
    const target = actionsRef.current?.querySelector<HTMLElement>(`[data-control="${control}"]`);
    if (!target) return false;
    target.focus({ preventScroll: true });
    return document.activeElement === target;
  };
  const focusStayedOnTheActions = () =>
    document.activeElement === document.body ||
    actionsRef.current?.contains(document.activeElement) === true;
  const requestFocus = (control: string) => {
    pendingFocusRef.current = focusActionControl(control) ? null : control;
  };
  useLayoutEffect(() => {
    const control = pendingFocusRef.current;
    if (control === null) return;
    if (!focusStayedOnTheActions()) pendingFocusRef.current = null;
    else if (focusActionControl(control)) pendingFocusRef.current = null;
  });

  // One press adds one; when it lands the stepper replaces Add and focus follows to its
  // Increase, and a failed add hands focus back to Add. Story 8.3 (ADO-2012), D-19: an add
  // refused because a colleague added the product first leaves their line in the cart, so
  // the stepper replaces Add and focus follows as for a landed add.
  const addOne = () => {
    setFirstAddInFlight(true);
    void addAndFocus(1);
  };
  const addAndFocus = (quantity: number) =>
    confirm(quantity)
      .then((landed) => {
        const swapsToStepper =
          landed || (useBasketStore.getState().quantityBySku.get(product.sku) ?? 0) > 0;
        if (focusStayedOnTheActions())
          pendingFocusRef.current = swapsToStepper ? 'increase' : 'add';
      })
      .finally(() => setFirstAddInFlight(false));
  // Decrease at 1 and a typed 0 remove the line and bring Add back; a removal that does
  // not land leaves the stepper, and focus, where they were.
  const removeLine = async (control: string) => {
    const landed = setLineQuantity(product.sku, 0);
    requestFocus('add');
    if (await landed) return;
    pendingFocusRef.current = null;
    if (focusStayedOnTheActions()) requestFocus(control);
  };
  const decreaseLine = () => {
    if (inCart <= 1) void removeLine('decrease');
    else void setLineQuantity(product.sku, inCart - 1);
  };
  const increaseLine = () => void setLineQuantity(product.sku, inCart + 1);
  const commitTypedLine = ({ typed, trigger, focusTarget }: QuantityStepperCommit) => {
    const next = typedLineQuantity(typed, inCart);
    if (next === null) return;
    if (next > 0) {
      void setLineQuantity(product.sku, next);
      return;
    }
    const focusLeftTheControl =
      trigger === 'blur' && focusTarget !== null && !actionsRef.current?.contains(focusTarget);
    if (focusLeftTheControl) void setLineQuantity(product.sku, 0);
    else void removeLine('quantity');
  };

  // Story 8.3 (ADO-2012), Tasks 11.3 and 11.8: a refused add or change of this product's
  // line. No frame draws the hero, so the message sits where the hero shows its basket
  // error, under the actions (decided 2026-10-15). Its action is a new change to the line,
  // which ends the message; focus goes where the same change from the hero's own controls
  // would send it.
  const shownConflict = useLineConflictMessage(product.sku, { blocked: supplierUnavailable });
  const conflictAction = shownConflict?.action ?? null;
  const runConflictAction = (intent: ConflictIntent) => {
    if (intent.type === 'remove') {
      void removeLine('decrease');
      return;
    }
    if (intent.type === 'add') {
      setFirstAddInFlight(true);
      requestFocus('add');
      void addAndFocus(intent.quantity);
      return;
    }
    requestFocus('quantity');
    void setLineQuantity(product.sku, intent.quantity);
  };

  const [zoomOpen, setZoomOpen] = useState(false);

  // `imageUrl` is an UNVALIDATED supplier-feed value, and the zoom dialog shows it
  // full size, so it is scheme-gated to http(s) first (PR #168 review). Anything
  // else (a `javascript:`/`data:` value, a relative path, garbage) resolves to
  // `null` and takes the disabled branch below, the same as a product with no image.
  const zoomHref = safeImageHref(product.imageUrl);
  // ADO-3693: the HD copy, for the hero and the zoom alike (AC1, AC2), with the small image as
  // the fallback when it fails (AC3).
  const hdImage = useProductHdImage(product.sku, zoomHref);

  // ADO-2249 design follow-ups (3 Amigos, 2 Nov; A1, A2, A3): the hero's status band, the product
  // card's top strip. On order when the product is on an open order (the chip's full wording),
  // else in the cart whenever the cart holds it, as the old "N in cart" pill did (decided with
  // the story owner 2026-11-04: an unavailable product keeps Add but still shows the band); in
  // the cart and on order the on-order band wins (OQ-1, the designer's answer). Neither: no band
  // and no state border (A3).
  const pickedFresh = showsPickedFresh(product, availability);
  const heroState: 'on-order' | 'in-cart' | undefined =
    onOrder !== undefined && onOrderChipText(onOrder) !== ''
      ? 'on-order'
      : inCart > 0
        ? 'in-cart'
        : undefined;

  return (
    <section
      className={styles.hero}
      aria-label={`${product.name} details`}
      data-product-hero=""
      data-hero-state={heroState}
    >
      {heroState === 'on-order' && onOrder !== undefined && (
        <div className={styles.statusBand} data-status-band="on-order">
          <OnOrderChip product={onOrder} className={styles.statusBandChip} />
        </div>
      )}
      {heroState === 'in-cart' && (
        <div className={styles.statusBand} data-status-band="in-cart">
          <Icon name="cart" size={14} className={styles.statusBandIcon} />
          <span className={styles.statusBandText}>
            {`In cart · ${inCart} ${inCart === 1 ? 'case' : 'cases'}`}
          </span>
        </div>
      )}
      <div className={styles.imageColumn}>
        <div className={styles.image}>
          <ProductHeroImage src={product.imageUrl} alt={product.name} hd={hdImage} />
        </div>
        {/* Zoom affordance (DR-2). Opens the full-size image in a dialog when
            there IS one, so the page stays where it is on desktop and phone alike;
            disabled for the branded placeholder, and for a feed URL that is not
            http(s), so it is never a dead control and never shows an unsafe URL. */}
        {zoomHref === null ? (
          <button
            type="button"
            className={styles.zoom}
            disabled
            aria-label={`No larger image available for ${product.name}`}
          >
            <Icon name="search" size={18} />
          </button>
        ) : (
          <>
            <button
              type="button"
              className={styles.zoom}
              aria-label={`Zoom image of ${product.name}`}
              aria-haspopup="dialog"
              onClick={() => setZoomOpen(true)}
            >
              <Icon name="search" size={18} />
            </button>
            <Modal
              open={zoomOpen}
              onClose={() => setZoomOpen(false)}
              title={product.name}
              size="wide"
              animated
            >
              <img
                className={styles.zoomImage}
                src={hdImage.src ?? zoomHref}
                alt={product.name}
                onError={(event) => {
                  const failed = event.currentTarget.getAttribute('src');
                  if (failed !== null && failed === hdImage.src) hdImage.markFailed(failed);
                }}
              />
            </Modal>
          </>
        )}
      </div>

      <div className={styles.details}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{product.name}</h1>
          <button
            type="button"
            className={styles.heart}
            data-favourited={isFavourite}
            aria-pressed={isFavourite}
            disabled={favouritePending}
            aria-label={
              isFavourite
                ? `Remove ${product.name} from favourites`
                : `Add ${product.name} to favourites`
            }
            onClick={() => {
              if (isFavourite) void removeFavourite(product.sku);
              else void addFavourite(product.sku);
            }}
          >
            <Icon name="heart" size={24} />
          </button>
        </div>

        {/* ADO-2249 design follow-ups (A1 Mobile): the row only when it holds a pill, so no
            empty row sits between the name and the price. */}
        {(band !== undefined || pickedFresh) && (
          <div className={styles.pills} data-pills="">
            {band !== undefined && (
              <StatusBadge tone={BAND_PRESENTATION[band].tone}>
                {BAND_PRESENTATION[band].label}
              </StatusBadge>
            )}
            {/* No entry for a picked-fresh product (E7-B AVL-5.4): SL1.0's "Picked fresh" in
                its place, the same PickedFreshPill as the card (Enabler E7-C, ADO-2274, D-16). */}
            {pickedFresh && <PickedFreshPill>{PICKED_FRESH_LABEL}</PickedFreshPill>}
          </div>
        )}

        {/* Price row (Figma set 5b; ADO-2249 BWG feedback, 5 Nov): the price, the struck was
            price, the `Contract price` pill and the deposit. The figures a spoken price replaces
            are hidden from assistive tech (ADO-2249 DEC-15). */}
        <div className={styles.priceBlock} data-price-row="">
          <span className={styles.priceFigures} aria-hidden={spoken === null ? undefined : 'true'}>
            <ProductPrice
              sku={product.sku}
              productName={product.name}
              className={promotion ? `${styles.price} ${styles.promotion}` : styles.price}
            />
            {wasPrice !== undefined && <s className={styles.wasPrice}>{formatMoney(wasPrice)}</s>}
            {contract && <ContractPricePill />}
          </span>
          {spoken !== null && <span className={styles.visuallyHidden}>{spoken}</span>}
          {deposit !== null && <span className={styles.priceMeta}>{deposit}</span>}
        </div>

        {/* Detail strip (ADO-2249 BWG feedback, 5 Nov; Figma 2026-11-03-2): Pack/Case, RSP,
            Margin (the quote's with the flag on, the sample off, Story 11.10), Last Ordered (a
            placeholder, below). Pack/Case uses the real pack where the read model has one; RSP
            keeps its cell with an empty value while there is none. RPS was REMOVED by the PO on
            2026-07-29 (gap G3 closed: never defined, gone from the designs). */}
        <dl className={styles.metaTable}>
          <div className={styles.metaCell}>
            {/* Bug 3738's label, as SL1.0 and the order review's PACK/CASE column; Figma's
                "Pack Case" is a question for the designer (ADO-2249, BWG feedback). */}
            <dt>Pack/Case</dt>
            <dd>{packCase}</dd>
          </div>
          <div className={styles.metaCell} data-rsp-cell="">
            <dt>RSP</dt>
            <dd>{rspValue}</dd>
          </div>
          {marginCell !== undefined && (
            <div className={styles.metaCell} data-margin-cell="">
              <dt>
                {marginInfo === null ? (
                  'Margin'
                ) : (
                  <MarginInfo label="Margin" content={marginInfo} size={marginInfoSize} />
                )}
              </dt>
              <dd>{marginCell}</dd>
            </div>
          )}
          <div className={styles.metaCell}>
            <dt>Last Ordered</dt>
            {/* A placeholder until Story 7.23 (ADO-1712) wires the real date: the cart's
                empty-cell glyph, which assistive tech hears as "not available yet". */}
            <dd>
              <span aria-hidden="true">{NEVER_ORDERED_GLYPH}</span>
              <span className={styles.visuallyHidden}>{NOT_AVAILABLE_YET}</span>
            </dd>
          </div>
        </dl>

        {/* Story 8.12 (ADO-2021) — On Order: every open order of the Customer holding this
            product, newest first, at any depot (AC2). Hidden when it is not on order. */}
        {onOrder !== undefined && (
          <div className={styles.onOrder} data-on-order-block="">
            <span className={styles.onOrderDot} aria-hidden="true" />
            <span className={styles.onOrderTitle}>On Order</span>
            <ul className={styles.onOrderList}>
              {onOrder.orders.map((order) => (
                <li key={order.orderId} className={styles.onOrderDetail}>
                  {onOrderDetailText(order, sessionDepotId)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ADO-2249 (Figma sets 5 and 5a): a promotion's offer strip, the deal in its own words,
            below the detail strip and above Add. */}
        {offerText !== null && (
          <p className={styles.offerStrip} data-offer-strip="">
            <Icon name="tag" size={14} />
            <span className={styles.offerStripText}>{offerText}</span>
          </p>
        )}

        {/* ADO-3985 T12 (frame 05): the multi-buy strip in the same place, the promotion strip's
            look with the gift leading. From 1024px the wide text then "See deal", the only
            button (styled as the list row's "See deal", T11); below 1024px the compact text with
            a trailing arrow, the whole strip one button. */}
        {shownDeal !== null &&
          (desktop ? (
            <p className={styles.offerStrip} data-multibuy-strip="wide">
              <Icon name="gift" size={14} />
              <span className={styles.dealBody}>
                <span className={styles.offerStripText}>
                  {withMoreOffers(wideDealText(shownDeal, wideFreeName), otherDeals)}
                </span>
                <button
                  type="button"
                  className={styles.seeDeal}
                  aria-label={seeDealName(shownDeal, product.name)}
                  onClick={seeDeal}
                >
                  See deal
                  <Icon name="arrowRight" size={14} />
                </button>
              </span>
            </p>
          ) : (
            <button
              type="button"
              className={`${styles.offerStrip} ${styles.dealStrip}`}
              data-multibuy-strip="compact"
              aria-label={compactStripName(
                withMoreOffers(compactDealText(shownDeal), otherDeals),
                product.name,
              )}
              onClick={seeDeal}
            >
              <Icon name="gift" size={14} />
              <span className={styles.offerStripText}>
                {withMoreOffers(compactDealText(shownDeal), otherDeals)}
              </span>
              <Icon name="arrowRight" size={14} />
            </button>
          ))}

        <div className={styles.actions} ref={actionsRef}>
          {showsCartStepper ? (
            <QuantityStepper
              className={styles.cartStepper}
              size="adaptive"
              value={inCart}
              label={`Cart quantity for ${product.name}`}
              decreaseLabel={`Decrease quantity in cart of ${product.name}`}
              increaseLabel={`Increase quantity in cart of ${product.name}`}
              onDecrease={decreaseLine}
              onIncrease={increaseLine}
              increaseDisabled={isAtLineMaximum(inCart)}
              busy={basketPending}
              decreaseControl="decrease"
              increaseControl="increase"
              editable={{
                inputLabel: `Quantity in cart of ${product.name}`,
                sanitiseInput: sanitiseQuantityInput,
                onCommit: commitTypedLine,
                control: 'quantity',
              }}
            />
          ) : (
            <Button
              variant="primary"
              className={styles.addButton}
              data-control="add"
              onClick={addOne}
              disabled={basketPending || firstAddInFlight || supplierUnavailable || priceBlocksAdd}
              aria-label={`Add ${product.name} to cart`}
            >
              Add
            </Button>
          )}
          {/* Story 11.12 (ADO-1963): the cheaper-on-WOW action, shown only while the product is
              cheaper on the store's WOW order; it adds to that order, never to this one. Keyed by
              the product: the page reuses the hero across products, and an add still settling
              for the last one must not hold this one's button. */}
          <WowOfferAction key={product.sku} sku={product.sku} productName={product.name} />
          <span className={styles.visuallyHidden} role="status">
            {confirmed ? `Added ${product.name} to cart` : ''}
          </span>
        </div>

        {supplierUnavailable && (
          <p className={styles.message} role="status">
            {SUPPLIER_UNAVAILABLE_MESSAGE}
          </p>
        )}
        {!supplierUnavailable && basketError !== null && (
          <p className={styles.message} role="alert">
            {basketError}
          </p>
        )}
        {shownConflict === null ? null : (
          <ConflictMessage
            message={shownConflict.message}
            {...(conflictAction === null
              ? {}
              : {
                  action: {
                    label: conflictAction.label,
                    onClick: () => runConflictAction(conflictAction.intent),
                  },
                })}
          />
        )}
      </div>
    </section>
  );
}
